/**
 * THE FRONT DOOR: polywog.io asks for your email and sends you to your
 * organization.
 *
 * The apex cannot sign anybody in. Identity Platform tenants are
 * per-org user pools -- the same email in two orgs is two distinct
 * users -- so there is no tenant to authenticate against until an org
 * is named. This screen names one and redirects; the real sign-in
 * happens on `{org}.polywog.io`, inside the tenant that subdomain
 * selects.
 *
 * THE EMAIL IS NOT LOOKED UP. An org hosts its own email domain, so
 * the address already carries the answer and orgFromEmail() reads it
 * in the browser -- no request, nothing to enumerate. An endpoint that
 * answered "does this address have an account, and at which firm"
 * would hand over the customer list of a product whose customers are
 * named engineering firms; deriving it locally asks nobody.
 *
 * Static: no API call, nothing to serve. The whole screen is a text
 * box and a redirect, which is why the apex can be a published React
 * site with no backend behind it at all.
 */
import { useState } from 'react';
import { ActionButton, DoorCard, ExternalLink, Field, Notice, Stack, Text } from '../../../strata';
import { invitedOrgFromPath, nextFromSearch, orgFromEmail, recordUrl } from '../host';
import { Brand } from './Brand';

const REFUSAL =
  'That address does not name an organization we can send you to. Use your ' +
  "work email -- the one at your firm's own domain.";

export function Dispatch() {
  // An invite link lands here carrying the org (`/join/{org}`), so the
  // invited person never has to be told what to type. It is a prefill,
  // not an authorization: knowing the name grants nothing, because the
  // seat is held for a VERIFIED email the identity provider attests.
  const invited = invitedOrgFromPath(window.location.pathname);
  const [email, setEmail] = useState('');
  const [refused, setRefused] = useState(false);

  // An invite link already names the org, so the address is not needed
  // to find one -- it goes straight there.
  const org = invited ?? orgFromEmail(email);

  const go = () => {
    // Refused HERE rather than by a subdomain that does not resolve --
    // a DNS error is not a message.
    if (org === null) {
      setRefused(true);
      return;
    }
    window.location.assign(recordUrl(org, window.location, email.trim(), nextFromSearch(window.location.search)));
  };

  return (
    <DoorCard>
      <Stack direction="column" gap="xl">
        <Brand />
        <Stack direction="column" gap="md">
          {/* WHAT TO TYPE AND WHAT HAPPENS, and nothing else. WORK email
              is the part that earns its place: an address at the firm's
              own domain is what carries the organization, so a personal
              one cannot resolve. An INVITATION says something different,
              because it names an org the person may never have typed. */}
          <Text tone="secondary">
            {invited ? (
              <>
                You have been invited to <strong>{invited}</strong>.
              </>
            ) : (
              <>Enter your work email and we&rsquo;ll take you to your organization.</>
            )}
          </Text>
          <Field
            label="Work email"
            labelHidden
            type="email"
            value={email}
            onChange={(value) => {
              setEmail(value);
              setRefused(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') go();
            }}
            placeholder="you@your-firm.com"
            autoFocus
            autoComplete="email"
            isInvalid={refused}
          />
          {/* Named before anybody commits: the address is a GUESS at the
              org, and a wrong guess should be visible while it is still
              one keystroke from being fixed. */}
          {org !== null && !invited && (
            <Text tone="secondary">
              Continuing to <strong>{org}</strong>.
            </Text>
          )}
          <ActionButton variant="brand" onPress={go}>
            Continue
          </ActionButton>
          {refused && <Notice kind="error">{REFUSAL}</Notice>}
          {/* SHOWN TO EVERYONE, not just after a refusal. Somebody whose
              firm has no workspace should find the way in BEFORE they
              type an address and hit a wall -- and this way the front
              door never has to know which orgs exist, so there is no list
              to leak. */}
          <Text tone="secondary" size="caption">
            No workspace yet? Email{' '}
            <ExternalLink href="mailto:holly@polywog.io?subject=Registering%20our%20organization">holly@polywog.io</ExternalLink> to
            register your organization.
          </Text>
        </Stack>
      </Stack>
    </DoorCard>
  );
}
